<p-toast></p-toast>

<!-- ============================================================
     TOP SECTION — Deposit Selector
     ============================================================ -->
<p-card styleClass="mb-3">
    <div class="section-header">QB Deposit Enrichment</div>

    <div class="p-fluid p-formgrid p-grid p-align-end">
        <div class="p-field p-col-12 p-md-3">
            <label for="startDate">Start Date</label>
            <p-calendar
                id="startDate"
                [(ngModel)]="startDate"
                dateFormat="yy-mm-dd"
                [showIcon]="true"
                appendTo="body">
            </p-calendar>
        </div>
        <div class="p-field p-col-12 p-md-3">
            <label for="endDate">End Date</label>
            <p-calendar
                id="endDate"
                [(ngModel)]="endDate"
                dateFormat="yy-mm-dd"
                [showIcon]="true"
                appendTo="body">
            </p-calendar>
        </div>
        <div class="p-field p-col-12 p-md-3" style="padding-top:1.5rem;">
            <p-button
                [label]="depositsLoading ? 'Loading deposits…' : 'Load Unlabeled Deposits'"
                icon="pi pi-search"
                (onClick)="loadUnlabeledDeposits()"
                [loading]="depositsLoading"
                [disabled]="depositsLoading">
            </p-button>
        </div>
    </div>

    <!-- Big visible loading state for deposit list fetch -->
    <div *ngIf="depositsLoading" style="text-align:center;padding:2rem;border:1px dashed #ccc;border-radius:6px;margin-top:1rem;">
        <p-progressSpinner [style]="{'width':'50px','height':'50px'}"></p-progressSpinner>
        <div style="margin-top:0.75rem;color:#333;font-weight:600">Loading deposits from QuickBooks Online…</div>
        <div style="margin-top:0.25rem;color:#777;font-size:0.85rem">Fetching deposits between {{ formatDate(startDate) }} and {{ formatDate(endDate) }}</div>
    </div>

    <!-- Empty-state message when load completed but found nothing -->
    <div *ngIf="!depositsLoading && depositsLoadedOnce && deposits.length === 0" style="text-align:center;padding:1.5rem;color:#777;border:1px dashed #ccc;border-radius:6px;margin-top:1rem;">
        No unlabeled deposits found in that date range.
    </div>

    <!-- Deposit list table -->
    <div class="deposit-table-wrapper" *ngIf="deposits.length > 0">
        <p-table
            [value]="deposits"
            [loading]="depositsLoading"
            styleClass="p-datatable-sm p-datatable-striped"
            [paginator]="deposits.length > 20"
            [rows]="20">
            <ng-template pTemplate="header">
                <tr>
                    <th>Txn Date</th>
                    <th style="text-align:right">Total</th>
                    <th style="text-align:center">Lines</th>
                    <th style="text-align:center">Unlabeled</th>
                    <th>Account</th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-dep>
                <tr>
                    <td>{{ dep.txnDate }}</td>
                    <td class="amount-cell">{{ formatCurrency(dep.totalAmt) }}</td>
                    <td style="text-align:center">{{ dep.lineCount }}</td>
                    <td style="text-align:center">
                        <span class="unlabeled-count-badge" *ngIf="dep.unlabeledLineCount > 0">
                            {{ dep.unlabeledLineCount }}
                        </span>
                        <span *ngIf="dep.unlabeledLineCount === 0" style="color:#2e7d32">&#10003;</span>
                    </td>
                    <td>{{ dep.depositAccount || 'Bank of Hawaii - 4993' }}</td>
                    <td>
                        <p-button
                            label="Open"
                            icon="pi pi-folder-open"
                            styleClass="p-button-sm p-button-outlined"
                            (onClick)="openDeposit(dep)">
                        </p-button>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</p-card>

<!-- ============================================================
     MIDDLE SECTION — Deposit Enrichment Table
     ============================================================ -->
<p-card styleClass="mb-3" *ngIf="matchResult || matchLoading">

    <div *ngIf="matchLoading" style="text-align:center;padding:2rem;">
        <p-progressSpinner></p-progressSpinner>
        <div style="margin-top:0.5rem;color:#555">Loading deposit lines…</div>
    </div>

    <ng-container *ngIf="matchResult && !matchLoading">
        <!-- Deposit header -->
        <div class="deposit-meta">
            <span><strong>Deposit ID:</strong> {{ matchResult.qboDepositId }}</span>
            <span><strong>Date:</strong> {{ matchResult.txnDate }}</span>
            <span><strong>Account:</strong> Bank of Hawaii - 4993</span>
            <span><strong>Lines:</strong> {{ matchResult.lineCount }}</span>
            <span><strong>Unlabeled:</strong> {{ matchResult.unlabeledLineCount }}</span>
        </div>

        <!-- Line table -->
        <p-table
            [value]="lineRows"
            styleClass="p-datatable-sm p-datatable-striped"
            [scrollable]="true"
            scrollHeight="60vh">
            <ng-template pTemplate="header">
                <tr>
                    <th style="width:60px">Line #</th>
                    <th style="width:100px;text-align:right">Amount</th>
                    <th style="width:220px">Current Label</th>
                    <th style="width:260px">Suggested Label</th>
                    <th style="width:80px;text-align:center">Apply</th>
                    <th>Candidates</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <!-- Line # -->
                    <td>{{ row.lineNum }}</td>

                    <!-- Amount -->
                    <td class="amount-cell">{{ formatCurrency(row.amount) }}</td>

                    <!-- Current label -->
                    <td>
                        <div *ngIf="row.labeled" class="labeled-cell">
                            <div>{{ row.currentEntity }}</div>
                            <div *ngIf="row.currentClass" style="color:#555">{{ row.currentClass }}</div>
                            <div *ngIf="row.currentAccount" style="color:#777;font-size:0.75rem">{{ row.currentAccount }}</div>
                        </div>
                        <span *ngIf="!row.labeled" class="unlabeled-cell">—</span>
                    </td>

                    <!-- Suggested label -->
                    <td>
                        <ng-container *ngIf="!row.labeled">
                            <!-- Manual override active -->
                            <div *ngIf="row.manualEntity" class="suggested-cell">
                                <span class="confidence-badge MANUAL" style="background:#e3f2fd;color:#1565c0">MANUAL</span>
                                <div>{{ row.manualEntity }}</div>
                                <div style="color:#555">{{ row.manualClass }}</div>
                                <div style="color:#777;font-size:0.75rem">{{ row.manualAccount }}</div>
                            </div>
                            <!-- Backend suggestion -->
                            <div *ngIf="!row.manualEntity && row.suggestedEntity" class="suggested-cell">
                                <span class="confidence-badge {{ row.confidence }}">{{ row.confidence }}</span>
                                <div>{{ row.suggestedEntity }}</div>
                                <div style="color:#555">{{ row.suggestedClass }}</div>
                                <div style="color:#777;font-size:0.75rem">{{ row.suggestedAccount }}</div>
                            </div>
                            <span *ngIf="!row.manualEntity && !row.suggestedEntity" style="color:#aaa;font-size:0.8rem">No suggestion</span>
                        </ng-container>
                    </td>

                    <!-- Apply checkbox -->
                    <td style="text-align:center">
                        <p-checkbox
                            *ngIf="!row.labeled"
                            [(ngModel)]="row.applyChecked"
                            [binary]="true">
                        </p-checkbox>
                        <span *ngIf="row.labeled" style="color:#2e7d32">&#10003;</span>
                    </td>

                    <!-- Candidates / Manual button -->
                    <td>
                        <ng-container *ngIf="!row.labeled">
                            <!-- Candidate switcher if multiple -->
                            <p-dropdown
                                *ngIf="row.candidates && row.candidates.length > 1"
                                [options]="candidateOptions(row)"
                                [(ngModel)]="row.selectedCandidate"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Switch match…"
                                styleClass="p-inputtext-sm"
                                appendTo="body"
                                [style]="{'width':'100%','max-width':'260px','margin-bottom':'4px'}">
                            </p-dropdown>
                            <!-- Manual button for LOW or no suggestion -->
                            <p-button
                                *ngIf="row.confidence === 'LOW' || !row.suggestedEntity"
                                label="Manual…"
                                icon="pi pi-pencil"
                                styleClass="p-button-sm p-button-text p-button-warning"
                                (onClick)="openManualDialog(row)">
                            </p-button>
                        </ng-container>
                    </td>
                </tr>
            </ng-template>
        </p-table>

        <!-- Footer bar — bank-is-truth check -->
        <div class="footer-bar">
            <span>Sum of lines: <strong>{{ formatCurrency(sumOfLines) }}</strong></span>
            <span>Bank total: <strong>{{ formatCurrency(bankTotal) }}</strong></span>
            <span *ngIf="isPennyPerfect" class="match-ok">&#10003; Penny-perfect</span>
            <span *ngIf="!isPennyPerfect" class="match-fail">&#10008; Off by {{ formatCurrency(balanceDiff) }}</span>
        </div>

        <!-- ============================================================
             FOOTER SECTION — Apply Panel
             ============================================================ -->
        <div class="apply-panel">
            <label for="approvedBy" style="font-weight:600;white-space:nowrap">Approved by:</label>
            <input
                id="approvedBy"
                pInputText
                [(ngModel)]="approvedBy"
                style="width:220px"
                placeholder="Name">
            <p-button
                [label]="applyLoading ? 'Applying labels to QBO…' : 'Apply Selected Labels'"
                icon="pi pi-check"
                [disabled]="selectedLineCount === 0 || applyLoading"
                [loading]="applyLoading"
                (onClick)="applySelected()">
            </p-button>
            <span style="color:#555;font-size:0.85rem" *ngIf="selectedLineCount > 0">
                {{ selectedLineCount }} line(s) selected
            </span>
        </div>

        <!-- ============================================================
             BOTTOM SECTION — Audit Log (collapsible)
             ============================================================ -->
        <div class="audit-section">
            <p-panel
                header="Audit Log"
                [toggleable]="true"
                [collapsed]="!auditVisible"
                (onAfterToggle)="toggleAudit()">
                <div *ngIf="auditLoading" style="text-align:center;padding:1rem;">
                    <p-progressSpinner [style]="{'width':'30px','height':'30px'}"></p-progressSpinner>
                </div>
                <p-table
                    *ngIf="!auditLoading"
                    [value]="auditEntries"
                    styleClass="p-datatable-sm p-datatable-striped"
                    [paginator]="auditEntries.length > 25"
                    [rows]="25">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Applied At</th>
                            <th>Applied By</th>
                            <th>Line #</th>
                            <th>Entity</th>
                            <th>Class</th>
                            <th>Account</th>
                            <th>Confidence</th>
                            <th>Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-entry>
                        <tr>
                            <td>{{ entry.appliedAt }}</td>
                            <td>{{ entry.appliedBy }}</td>
                            <td>{{ entry.lineNum }}</td>
                            <td>{{ entry.entity }}</td>
                            <td>{{ entry.qbClass }}</td>
                            <td>{{ entry.account }}</td>
                            <td>
                                <span class="confidence-badge {{ entry.confidence }}">{{ entry.confidence }}</span>
                            </td>
                            <td>{{ entry.status }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8" style="text-align:center;color:#999;padding:1rem">No audit entries for this deposit.</td></tr>
                    </ng-template>
                </p-table>
            </p-panel>
        </div>
    </ng-container>
</p-card>

<!-- ============================================================
     Manual Label Dialog
     ============================================================ -->
<p-dialog
    header="Manual Label Entry"
    [(visible)]="manualDialogVisible"
    [modal]="true"
    [style]="{'width':'420px'}"
    appendTo="body">
    <div class="manual-entry-form" *ngIf="manualLine">
        <div style="font-size:0.85rem;color:#555;margin-bottom:0.5rem">
            Line #{{ manualLine.lineNum }} — {{ formatCurrency(manualLine.amount) }}
        </div>
        <div class="p-field">
            <label>Entity (Guest Name / Reservation #)</label>
            <input pInputText [(ngModel)]="manualEntityInput" style="width:100%" placeholder="e.g. Smith, John 123456">
        </div>
        <div class="p-field">
            <label>Class (Island:Property-Unit)</label>
            <input pInputText [(ngModel)]="manualClassInput" style="width:100%" placeholder="e.g. Maui:Honua-Kai-303">
        </div>
        <div class="p-field">
            <label>Account</label>
            <input pInputText [(ngModel)]="manualAccountInput" style="width:100%" placeholder="e.g. 1150040022">
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" styleClass="p-button-text" (onClick)="cancelManualDialog()"></p-button>
        <p-button label="Save" icon="pi pi-check" (onClick)="saveManualLabel()"></p-button>
    </ng-template>
</p-dialog>
